// Чистые помощники фасетного фильтра позиций спутников (раздел «Спутники»).
// Только тестируемая логика без MapLibre и React: описание измерений фильтра,
// обогащение свойств позиции полями реестра (company_key/legal_entity), сборка
// фильтруемого GeoJSON и построение выражения `setFilter`. UI-панель — FilterPanel.tsx,
// подключение к карте — SatellitesPage.tsx.

import type maplibregl from 'maplibre-gl'
import type { SatellitePosition, SatelliteRegistryItem } from '../api'

// Измерения фильтра (порядок = порядок групп в панели). Каждое измерение —
// независимый фасет; внутри измерения значения объединяются по ИЛИ, между
// измерениями — по И (см. buildFilterExpression/visibleCount).
export const FILTER_DIMS = ['category', 'company', 'orbit_class', 'legal_entity', 'status', 'source'] as const
export type FilterDim = (typeof FILTER_DIMS)[number]

// Состояние фильтра: на каждое измерение — список выбранных значений. Пустой
// список = измерение не фильтрует (пропускает всё).
export type FilterState = Record<FilterDim, string[]>

export const EMPTY_FILTER: FilterState = {
  category: [],
  company: [],
  orbit_class: [],
  legal_entity: [],
  status: [],
  source: [],
}

// Свойства фильтруемой фичи. Базовые поля — как у positionsToGeoJSON, плюс два
// обогащённых из реестра (в позициях их нет): company_key и legal_entity.
export interface FilterableProps {
  norad_id: number
  name: string
  category: string
  operator: string | null
  orbit_class: string | null
  status: string | null
  company_key: string | null
  legal_entity: string | null
  discovery_source: string | null
}

// Измерение фильтра → имя свойства фичи, по которому оно фильтрует. Ключ используется
// и в ['get', ...] выражения фильтра, и при индексировании FilterableProps.
export const FILTER_PROP: Record<FilterDim, keyof FilterableProps> = {
  category: 'category',
  company: 'company_key',
  orbit_class: 'orbit_class',
  legal_entity: 'legal_entity',
  status: 'status',
  source: 'discovery_source',
}

/** Индекс реестра `norad_id → запись` (строковый ключ — как у свойств фичи). */
export function buildRegistryIndex(
  items: SatelliteRegistryItem[],
): Record<string, SatelliteRegistryItem> {
  const idx: Record<string, SatelliteRegistryItem> = {}
  for (const it of items) idx[String(it.norad_id)] = it
  return idx
}

/** Свойства позиции + company_key/legal_entity из реестра. Спутника нет в реестре
 *  → оба поля null (фильтр по компании/юрлицу тогда его не пропустит). */
export function enrichPositionProps(
  pos: SatellitePosition,
  registryById: Record<string, SatelliteRegistryItem>,
): FilterableProps {
  const reg = registryById[String(pos.norad_id)]
  return {
    norad_id: pos.norad_id,
    name: pos.name,
    category: pos.category,
    operator: pos.operator,
    orbit_class: pos.orbit_class,
    status: pos.status,
    company_key: reg?.company_key ?? null,
    legal_entity: reg?.legal_entity ?? null,
    discovery_source: reg?.discovery_source ?? null,
  }
}

/** Позиции → FeatureCollection с обогащёнными свойствами (все 5 полей фильтра).
 *  Нечисловые координаты пропускаем — иначе MapLibre получил бы NaN. */
export function positionsToFilterableGeoJSON(
  positions: SatellitePosition[],
  registryById: Record<string, SatelliteRegistryItem>,
): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = []
  for (const p of positions) {
    if (!Number.isFinite(p.lat) || !Number.isFinite(p.lon)) continue
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [p.lon, p.lat] },
      properties: enrichPositionProps(p, registryById),
    })
  }
  return { type: 'FeatureCollection', features }
}

/** Выражение MapLibre `setFilter` из состояния фильтра. Все измерения пусты →
 *  null (фильтр снят, показываем всё). Иначе И по активным измерениям, внутри
 *  каждого — ИЛИ через ['in', значение, ['literal', массив]]. */
export function buildFilterExpression(
  state: FilterState,
): maplibregl.ExpressionSpecification | null {
  const active = FILTER_DIMS.filter(d => state[d].length > 0)
  if (active.length === 0) return null
  const clauses = active.map(d => ['in', ['get', FILTER_PROP[d]], ['literal', [...state[d]]]])
  return ['all', ...clauses] as unknown as maplibregl.ExpressionSpecification
}

/** Активен ли фильтр хотя бы по одному измерению. */
export function isFilterActive(state: FilterState): boolean {
  return FILTER_DIMS.some(d => state[d].length > 0)
}

/** Переключить значение измерения (добавить/убрать) — иммутабельно. */
export function toggleFilterValue(state: FilterState, dim: FilterDim, value: string): FilterState {
  const cur = state[dim]
  const next = cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value]
  return { ...state, [dim]: next }
}

/** Сколько всего значений выбрано по всем измерениям (для бейджа панели). */
export function countActive(state: FilterState): number {
  return FILTER_DIMS.reduce((n, d) => n + state[d].length, 0)
}

/** Сколько позиций пройдёт фильтр (для шапки панели). Считаем только фичи с
 *  числовыми координатами — именно они попадают на карту. И между измерениями,
 *  ИЛИ внутри измерения; null-свойство измерение не проходит (как ['in'] в MapLibre). */
export function visibleCount(
  positions: SatellitePosition[],
  registryById: Record<string, SatelliteRegistryItem>,
  state: FilterState,
): number {
  const active = FILTER_DIMS.filter(d => state[d].length > 0)
  let n = 0
  for (const p of positions) {
    if (!Number.isFinite(p.lat) || !Number.isFinite(p.lon)) continue
    if (active.length === 0) {
      n++
      continue
    }
    const props = enrichPositionProps(p, registryById)
    const ok = active.every(d => {
      const v = props[FILTER_PROP[d]]
      return typeof v === 'string' && state[d].includes(v)
    })
    if (ok) n++
  }
  return n
}
